import React, {
  forwardRef,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes } from "utils/formUtils";
import GenericMappingForm from "../GenericMappingForm";
import _, { isEmpty, map, uniqBy } from "lodash";
import message from "components/CustomMessage";
import { useForm } from "antd/lib/form/Form";

const SDElementsMappingForm = forwardRef(function SDElementsMappingForm(
  {disableProjectFields = false, ...props},
  ref
) {
  const projectRef = useRef({});
  const [mappedProjectList] = useState(() => {
    //using mappedProjectList to preserve the selected project when API fails
    if (props.record) {
      const { projectId, projectName, applicationId } = props.record;
      if (!isEmpty(projectId) && !isEmpty(projectName)) {
        const projectIdArray = projectId?.split(",");
        const projectNameArray = projectName?.split(",");
        projectRef.current[applicationId] = {};
        return {
          [applicationId]: map(projectIdArray, (id, index) => {
            const name = projectNameArray[index];
            projectRef.current[applicationId][id] = name
            return {
              id,
              name,
            }
          }),
        };
      }
    }
    return {};
  });

  const [initialValues] = useState(() => {
    if (props.record) {
      return undefined;
    }
    return [
      {
        product: props.defaultProduct,
        subProduct: props.defaultSubProduct,
        subProductId: props.defaultSubProductId,
        environment: props.defaultEnvironment,
        relevant: ["True", "False"]
      },
    ];
  });

  const [mappedApplicationList] = useState(() => {
    //using mappedProjectList to preserve the selected project when API fails
    if (props.record) {
      const { applicationId, applicationName } = props.record;
      return { projects: [{ id: applicationId, name: applicationName }] };
    }
    return { projects: [] };
  });
  const [form] = useForm();
  const applicationsRef = useRef({ projects: mappedApplicationList.projects });
  const apiParamsRef = useRef({});
  const fetchApplications = useCallback((params) => {
    const { promise, cancel } =
      ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
        login_id: props.loginConfigId,
        ...params
      });
    promise.then((response) => {
      let { projects } = response;
      if (mappedApplicationList["projects"].length > 0 && !isEmpty(projects)) {
        response.projects = uniqBy(
          [...projects, ...mappedApplicationList["projects"]],
          "id"
        );
      }
      applicationsRef.current["projects"] = uniqBy([...applicationsRef.current["projects"], ...projects], 'id');
    });

    return { promise, cancel };
  }, [props.loginConfigId, props.toolName, mappedApplicationList]);

  const fetchProjects = useCallback((params) => {
    if (!params.organisation) {
      return { promise: Promise.resolve([]), cancel: _.noop };
    }
    const { promise, cancel } =
      ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
        login_id: props.loginConfigId,
        ...params
      });
    promise
      .then((response) => {
        const { organisation } = params;
        if(!_.isEmpty(mappedProjectList[organisation]) && !isEmpty(response.projects)){
          response.projects = [...mappedProjectList[organisation], ...response.projects];
        }
        if (!projectRef.current[organisation]) {
          projectRef.current[organisation] = {};
        }
        _.forEach(response.projects, ({ id, name }) => {
          projectRef.current[organisation][id] = name;
        })
      })
      .catch((err) => {
          message.error("Problem fetching projects");
      });

    return { promise, cancel };
  }, [
    props.loginConfigId,
    props.toolName,
    mappedProjectList,
  ]);

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    return { 
      applicationId: config.applicationId,
      projectId: !isEmpty(config.projectId) ? config.projectId.split(",") : undefined,
      relevant: config.relevant.split(","),
      //send the property received in config
      ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
   };
  }, []);

  const getExtraFieldsData = useCallback((config) => {
    const { projectId, applicationId, relevant } = config;
    const projectIdString = projectId.join(",");
    const projectNames =_.map(projectId, (prId) => projectRef.current[applicationId][prId]);
    const applicationName = applicationsRef.current.projects.find(
      (project) => project.id === applicationId
    )?.name;
    return {
      projectId: projectIdString,
      projectName: projectNames.join(","),
      applicationId,
      applicationName,
      relevant: relevant.join(",")
    };
  }, []);

  const onApplicationIdChange = useCallback((value, field)=>{
    const {name} = field;
    const configurations = form.getFieldValue("configurations");
    let updatedConfiguration = [
      ...configurations,
    ];
    updatedConfiguration[name] = {
      ...updatedConfiguration[name],
      projectId: undefined,
    }
    form.setFieldsValue({ configurations: updatedConfiguration });
  },[form])

  const extraFields = useMemo(
    () => [
      {
        name: "applicationId",
        colSpan: "12",
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        label: "Application Name",
        required: true,
        disabled: disableProjectFields,
        help: "Enter full application name for filtering support",
        props: {
          autoFocus: true,
          api: fetchApplications,
          onChange: onApplicationIdChange
        },
      },
      {
        name: "projectId",
        colSpan: "12",
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        label: "Project Name",
        required: true,
        help: "Enter full project name for filtering support",
        disabled: disableProjectFields,
        props: {
          autoFocus: true,
          api: fetchProjects,
          setExtraProps: (fieldProps, currentField) => {
            const configurations = form.getFieldValue("configurations");
            const currentConfiguration = configurations[currentField.name] || {};
            const { applicationId } = currentConfiguration;
            const apiParams = apiParamsRef.current;
            const keyName = `${currentField.name}_${applicationId}`;
            if (!apiParams[keyName] && applicationId) {
              apiParams[keyName] = { organisation: applicationId };
            }
            return {
              apiParams: apiParams[keyName],
              resetOptionsProp: apiParams[keyName],
            }
          },
          mode: "multiple",
        },
      },{
        name: "relevant",
        colSpan: "12",
        label: "Countermeasure Relevance",
        disabled: disableProjectFields,
        type: FieldTypes.MULTI_SELECT,
        options: ["True", "False"],
        required: true
      }
    ],
    [fetchProjects, fetchApplications, form, onApplicationIdChange, disableProjectFields]
  );

  return (
    <GenericMappingForm
      form={form}
      ref={ref}
      getExtraFieldsData={getExtraFieldsData}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      extraFields={extraFields}
      formDefaultValues={initialValues}
      {...props}
    />
  );
});

export default SDElementsMappingForm;
